/** Thin fetch wrapper. Errors from the server are surfaced verbatim; nothing is ever faked locally. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: { fields?: Record<string, string> } & Record<string, unknown>,
  ) {
    super(message);
  }
  get fields(): Record<string, string> {
    return (this.details?.fields as Record<string, string>) || {};
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Json = Record<string, unknown> | unknown[] | null;

async function parse(res: Response): Promise<any> {
  const text = await res.text();
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const e = body?.error;
    throw new ApiError(
      res.status,
      e?.code || "http_error",
      e?.message || (res.status >= 500 ? "The service is temporarily unavailable. Nothing was saved — please try again." : `Request failed (${res.status}).`),
      e?.details,
    );
  }
  return body;
}

async function request<T>(method: string, url: string, body?: Json | FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: payload, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "network_error", "We could not reach Seva Sangrah. Check your connection — nothing was saved.");
  }
  return parse(res);
}

export const api = {
  get: <T = any>(url: string) => request<T>("GET", url),
  post: <T = any>(url: string, body?: Json | FormData) => request<T>("POST", url, body ?? {}),
  put: <T = any>(url: string, body?: Json) => request<T>("PUT", url, body ?? {}),
  patch: <T = any>(url: string, body?: Json) => request<T>("PATCH", url, body ?? {}),
  del: <T = any>(url: string) => request<T>("DELETE", url),
};

export function qs(params: Record<string, string | number | undefined | null | boolean>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "" && v !== false) s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : "";
}

export async function downloadCsv(url: string, body: Json, filename: string): Promise<number> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}) },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) await parse(res);
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
  return Number(res.headers.get("X-Row-Count") || 0);
}
